import React, { useEffect, useMemo, useRef, useState } from "react";
import { Building2, Check, ChevronDown, Globe2, Search } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import StatusBadge from "@/components/shared/StatusBadge";
import { useDebounce } from "@/hooks/useDebounce";
import { fmtCompact, fmtPct, pctOf, tenantName } from "@/components/usage/format";

/**
 * The scope control: the whole fleet, or one tenant.
 *
 * It is a purpose-built list rather than the shared `Select` because the
 * choice itself carries data. A SuperAdmin opening this page is looking for
 * the heavy tenant, so the list is **ranked by consumption** and every row
 * shows its tokens and its share — the answer is often visible before anything
 * is picked.
 *
 * Tenants with no usage in the window are still listed, under their own
 * heading. They are the ones somebody comes here to check on, and a picker that
 * hid them would look broken to the person asking "why is this tenant quiet?".
 *
 * Filtering is local. The full tenant list is already in hand — one page-load
 * fetch — so a round trip per keystroke would buy nothing.
 */
const TenantScopePicker = ({ tenants = [], value, onChange, fleetTokens, disabled }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  // Local, but still debounced: the list rebuilds two groups over every tenant
  // on the platform, and doing that per keystroke made typing feel sticky.
  const debouncedSearch = useDebounce(search, 200);
  const containerRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setSearch("");
      return undefined;
    }
    const onClickOutside = (e) => {
      if (!containerRef.current?.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onKeyDown);
    const timer = setTimeout(() => searchRef.current?.focus(), 0);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onKeyDown);
      clearTimeout(timer);
    };
  }, [open]);

  const selected = tenants.find((t) => t.id === value) ?? null;

  const { active, quiet } = useMemo(() => {
    const keyword = debouncedSearch.trim().toLowerCase();
    const matches = keyword ? tenants.filter((t) => `${t.name ?? ""} ${t.slug ?? ""}`.toLowerCase().includes(keyword)) : tenants;
    return {
      active: matches.filter((t) => t.totalTokens > 0 || t.uses > 0),
      quiet: matches.filter((t) => !(t.totalTokens > 0 || t.uses > 0)),
    };
  }, [tenants, debouncedSearch]);

  const Row = ({ tenant }) => (
    <Button
      onClick={() => {
        onChange(tenant.id);
        setOpen(false);
      }}
      className={`flex w-full items-center gap-3 rounded-none px-3 py-2 text-left transition-colors hover:bg-background-tertiary ${
        // The selected row must differ from the merely hovered one, so it
        // carries a ring rather than the same fill.
        value === tenant.id ? "bg-background-tertiary ring-1 ring-inset ring-tone-blue/50" : ""
      }`}
    >
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span className="truncate text-xs font-medium text-text-primary">{tenantName(tenant.name, tenant.slug, tenant.id)}</span>
          {value === tenant.id && <Check className="h-3 w-3 flex-shrink-0 text-tone-blue" />}
        </span>
        <span className="flex items-center gap-1.5">
          {tenant.slug && tenant.slug !== tenant.name && <span className="min-w-0 truncate text-[10px] text-text-secondary">{tenant.slug}</span>}
          {/* Same rule as the tenant header: mark only the rows that need
              explaining, and let Deleted win over Inactive. A tenant can be
              gone and still own the tokens it burned, so the row stays — it
              just has to say what it is. The slug gives way before the mark
              does: a truncated slug is still readable, half a pill is not. */}
          {(tenant.isDeleted || tenant.isActive === false) && (
            <span className="flex-shrink-0">
              <StatusBadge size="sm" isActive={tenant.isActive} isDeleted={tenant.isDeleted} />
            </span>
          )}
        </span>
      </span>
      {tenant.totalTokens > 0 ? (
        <span className="flex flex-shrink-0 flex-col items-end gap-1">
          <span className="text-[11px] text-text-secondary tabular-nums">{fmtCompact(tenant.totalTokens)}</span>
          <span className="h-1 w-14 overflow-hidden rounded-full bg-background-tertiary">
            <span className="block h-full rounded-full bg-tone-blue" style={{ width: `${Math.max(3, pctOf(tenant.totalTokens, fleetTokens))}%` }} />
          </span>
        </span>
      ) : (
        <span className="flex-shrink-0 text-[10px] text-text-secondary">no usage</span>
      )}
    </Button>
  );

  const GroupLabel = ({ children }) => (
    <p className="bg-background-primary px-3 py-1.5 text-[10px] font-medium uppercase tracking-wide text-text-secondary">{children}</p>
  );

  return (
    <div className="relative w-full" ref={containerRef}>
      <Button
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex h-9 w-full items-center gap-2 rounded-lg border border-border-primary bg-background-primary px-2.5 py-0 text-sm text-text-primary transition-colors hover:border-tone-blue/60 disabled:opacity-60"
      >
        {selected ? <Building2 className="h-4 w-4 flex-shrink-0 text-text-secondary" /> : <Globe2 className="h-4 w-4 flex-shrink-0 text-text-secondary" />}
        <span className="min-w-0 flex-1 truncate text-left">{selected ? tenantName(selected.name, selected.slug, selected.id) : "All tenants"}</span>
        <ChevronDown className={`h-4 w-4 flex-shrink-0 text-text-secondary transition-transform ${open ? "rotate-180" : ""}`} />
      </Button>

      {open && (
        <div className="absolute left-0 right-0 z-50 mt-1 flex max-h-[22rem] flex-col overflow-hidden rounded-lg border border-border-primary bg-background-secondary shadow-xl">
          <div className="relative border-b border-border-primary">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-secondary" />
            <Input
              ref={searchRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name or slug"
              className="h-9 rounded-none border-0 bg-transparent py-0 pl-9 pr-3 text-xs placeholder:text-text-secondary"
            />
          </div>

          <div className="overflow-y-auto">
            <Button
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
              className={`flex w-full items-center gap-3 rounded-none px-3 py-2.5 text-left transition-colors hover:bg-background-tertiary ${
                !value ? "bg-background-tertiary ring-1 ring-inset ring-tone-blue/50" : ""
              }`}
            >
              <Globe2 className="h-4 w-4 flex-shrink-0 text-text-secondary" />
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5 text-xs font-medium text-text-primary">
                  All tenants
                  {!value && <Check className="h-3 w-3 text-tone-blue" />}
                </span>
                <span className="block text-[10px] text-text-secondary">Fleet totals and the tenant comparison</span>
              </span>
              <span className="flex-shrink-0 text-[11px] text-text-secondary tabular-nums">{fmtCompact(fleetTokens)}</span>
            </Button>

            {active.length > 0 && (
              <>
                <GroupLabel>Active in this window · ranked by tokens</GroupLabel>
                {active.map((tenant) => (
                  <Row key={tenant.id} tenant={tenant} />
                ))}
              </>
            )}

            {quiet.length > 0 && (
              <>
                <GroupLabel>No usage in this window</GroupLabel>
                {quiet.map((tenant) => (
                  <Row key={tenant.id} tenant={tenant} />
                ))}
              </>
            )}

            {active.length === 0 && quiet.length === 0 && <p className="px-3 py-6 text-center text-xs text-text-secondary">No tenant matches that search.</p>}
          </div>

          {fleetTokens > 0 && selected && (
            <p className="border-t border-border-primary px-3 py-1.5 text-[10px] text-text-secondary">
              {tenantName(selected.name, selected.slug, selected.id)} is {fmtPct(pctOf(selected.totalTokens, fleetTokens))} of fleet tokens in this window.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default TenantScopePicker;
